import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, Injectable, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, RouterModule } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { HttpClient, HttpContext,  provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';
import { HttpServiceService } from './services/http-service.service';
import { httpErrorInterceptor } from './services/http-error.service';
import { configUrl } from '../assets/config/constants.json';
import { SKIP_INTERCEPTOR } from './services/loading.service';
import { MetadataResponse } from './models/users.model';
import { LocalStorageService } from './services/local-storage.service';



interface Config {
  baseUrl? : string
}

@Injectable({ providedIn: 'root' })
export class AppInitService {
  private http = inject(HttpClient);
  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);  

  async loadAppConfig(): Promise<any> {
    try {
      const context  = new HttpContext().set(SKIP_INTERCEPTOR, true);
      const config : Config = await firstValueFrom(this.http.get(configUrl , { context  } ));
      this.httpService.setBaseUrl(`${config.baseUrl}`);
      if(config?.baseUrl){
        const metadata : MetadataResponse = await firstValueFrom(this.httpService.getMetadata());
        let logo = metadata.data?.["logo"] ?? "";
        this.httpService.setLogoUrl(logo);
        // Separate logo for the post-login header bar; falls back to the
        // login-page logo when the partner hasn't configured one.
        let headerLogo = metadata.data?.["header_logo"] ?? "";
        this.httpService.setHeaderLogoUrl(headerLogo || logo);
        this.applyBrowserTabBranding(
          metadata.data?.["name"] ?? "",
          metadata.data?.["favicon"] ?? ""
        );
        if (this.authService.isLoggedIn()) {
          const permissions = await firstValueFrom(this.httpService.getUserPermissions());
          this.authService.setUserPermissions(permissions.data?.["permissions"] ?? []);
        }
      }
    } catch (error) {
      console.error('Failed to load config', error);
    }
  }

  /**
   * Point the browser tab at the partner. Both the icon and the title fall
   * back to whatever index.html shipped with, so a partner that has not
   * configured a favicon keeps the bundled default rather than losing its icon.
   */
  private applyBrowserTabBranding(partnerName: string, faviconUrl: string): void {
    if (partnerName) {
      document.title = partnerName;
    }
    if (!faviconUrl) return;

    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    // A partner favicon may be any image type, so drop the ico-specific hint
    // from index.html and let the browser sniff the actual format.
    link.removeAttribute('type');
    link.href = faviconUrl;
  }
}

function initializeApp() {
  return () => {
    const appInitService = inject(AppInitService);
    return appInitService.loadAppConfig();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
    },
    provideHttpClient(withInterceptors([httpErrorInterceptor])),    
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideAnimations(),
    importProvidersFrom(
      BrowserAnimationsModule,
      ToastrModule.forRoot(),
      RouterModule.forRoot(routes, {
        scrollPositionRestoration: 'top'
      }),      
    ),    
  ]
};
